import { useEffect, useMemo, useState, useCallback } from "react";
import { RightWrapper } from "../../../components";
import { MiniTabs, DataTable, InputField, Button } from "@/components";
import { useTabFilteredData, useApi, useAlert } from "@/";
import ColumnHelper from "@/utils/ColumnHelper";
import { useSelector } from "react-redux";

const ShippingId = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [existingData, setExistingData] = useState([]);
  const [generateCount, setGenerateCount] = useState("");
  const [isGeneratingCSV, setIsGeneratingCSV] = useState(false);
  const { callApi, loading } = useApi();
  const { alert, AlertDialog } = useAlert();
  const shippingRange = useSelector((state) => state.user?.user?.shippingRange);
  const clientId = useSelector((state) => state.user?.user?.clientId) || "";

  console.log("ClientID: ", clientId);
  console.log("Shipping Range:", shippingRange);

  const fetchData = useCallback(async () => {
    try {
      const res = await callApi("/user/getShippingId", "GET");
      console.log("Shipping Response:", res);
      if (!res.success) {
        if (res.errorObj?.code === "SHIPPING_CONFIG_NOT_FOUND") {
          await alert(
            "Please complete your KYC verification to enable shipping ID generation."
          );
        } else {
          await alert(
            res.error?.message ||
              res.errorObj?.message ||
              res.error ||
              "Failed to fetch shipping data"
          );
        }
        return;
      }
      setExistingData(res.data.shippingIds);
    } catch (err) {
      console.error("Fetch error:", err);
      alert(`${err.error?.message || err}`);
    }
  }, [callApi]);

  useEffect(() => {
    console.log("Fetching shipping data...");
    fetchData();
  }, [fetchData]);

  const generateShippingData = useCallback(
    (userId, prefixCount, start, end, existingData = []) => {
      const existingMap = new Map(
        existingData.map((item) => [item.shippingId, item])
      );
      const data = [];

      if (start < 1 || start > 99999 || end < 1 || end > 99999) {
        throw new Error("Start and end must be between 1 and 99999");
      }

      for (let p = 0; p <= prefixCount; p++) {
        const formattedPrefix = `P${String(p).padStart(4, "0")}`;

        for (let i = start; i <= end; i++) {
          const num = String(i).padStart(6, "0");
          const shippingId = `${userId}-${formattedPrefix}-${num}`;

          if (existingMap.has(shippingId)) {
            data.push(existingMap.get(shippingId));
          } else {
            data.push({
              shippingId,
              isBooked: false,
              isUsed: false,
              status: "Unused",
            });
          }
        }
      }
      return data;
    },
    []
  );

  useEffect(() => {
    console.log("Generating shipping data...");
    if (shippingRange) {
      try {
        const generatedData = generateShippingData(
          clientId,
          shippingRange.extensionCount,
          shippingRange.startRange,
          shippingRange.endRange,
          existingData
        );
        if (JSON.stringify(generatedData) !== JSON.stringify(existingData)) {
          setExistingData(generatedData);
        }
      } catch (err) {
        console.error("Data generation error:", err);
      }
    }
  }, [existingData, shippingRange]);

  const tabConfig = useMemo(
    () => ({
      idKey: "status",
      allTabId: "all",
      filters: {
        used: { type: "exact", value: "Used" },
        unused: { type: "exact", value: "Unused" },
      },
    }),
    []
  );

  const { data: finalFilteredData, isLoading } = useTabFilteredData(
    existingData,
    activeTab,
    tabConfig
  );

  const counts = useMemo(
    () => ({
      all: existingData.length,
      used: existingData.filter((item) => item.status === "Used").length,
      unused: existingData.filter((item) => item.status === "Unused").length,
    }),
    [existingData]
  );

  const tabs = useMemo(
    () => [
      { id: "all", label: "All", count: counts.all || 0 },
      { id: "used", label: "Used", count: counts.used || 0 },
      { id: "unused", label: "Unused", count: counts.unused || 0 },
    ],
    [counts]
  );

  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Shipping ID",
        accessorKey: "shippingId",
        meta: {
          tooltip: (row) => row.shippingId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Status",
        accessorKey: "status",
        cell: (info) =>
          info.row.original.status ? (
            info.row.original.status === "Used" ? (
              <div className="inline-flex group items-center gap-1 bg-red-50/80 px-2 py-0.5 rounded-full border border-red-100">
                <div className="w-2 h-2 bg-red-500 rounded-full group-hover:animate-pulse" />
                <span className="text-xs font-medium text-red-700 tracking-wide">
                  Used
                </span>
              </div>
            ) : (
              <div className="inline-flex group items-center gap-1 bg-green-50/80 px-2 py-0.5 rounded-full border border-green-100">
                <div className="w-2 h-2 bg-green-500 rounded-full group-hover:animate-pulse" />
                <span className="text-xs font-medium text-green-700 tracking-wide">
                  Unused
                </span>
              </div>
            )
          ) : (
            <div className="inline-flex items-center gap-1.5 bg-gray-50/80 px-3 py-1.5 rounded-full border border-gray-100">
              <div className="w-2 h-2 bg-gray-500 rounded-full" />
              <span className="text-sm font-semibold text-gray-700 tracking-wide">
                N/A
              </span>
            </div>
          ),
        meta: {
          tooltip: (row) => row.status || "N/A",
        },
      }),
    ],
    []
  );

  const generateShippingIds = () => {
    try {
      if (!generateCount || generateCount <= 0) {
        alert("Please enter a valid number of shipping IDs to generate.");
        return;
      }

      const unusedShippingIds = existingData.filter(
        (item) => item.status === "Unused"
      );

      if (unusedShippingIds.length < generateCount) {
        alert("Not enough unused shipping IDs available.");
        return;
      }

      setIsGeneratingCSV(true);
      
      const newShippingIds = unusedShippingIds.slice(0, generateCount).map((item) => item.shippingId);
      console.log("newShippingIds", newShippingIds);
      
      // Create CSV content with header and shipping IDs
      const csvHeader = "Shipping ID";
      const csvContent = [
        csvHeader,
        ...newShippingIds
      ].join('\n');
      
      // Create a Blob with the CSV content
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Create a download link and trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'shipping_ids_'+ new Date().getTime() + '.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      
      // Clean up resources
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setIsGeneratingCSV(false);
      }, 100);

      setGenerateCount("");
    } catch (error) {
      console.error("Error generating shipping IDs CSV:", error);
      alert(`Failed to generate CSV file: ${error.message || "Unknown error occurred"}`);
      setIsGeneratingCSV(false);
    }
  };

  return (
    <RightWrapper>
      <div className="flex flex-col gap-4 m-2 pb-20 md:pb-4">
        <div className=" w-full flex justify-between items-end gap-1">
        <MiniTabs
            tabs={tabs}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
            <div className="flex min-w-fit justify-start items-end gap-1">
              <InputField
                placeholder="Enter no. Ex. 10"
                type="number"
                className="!h-10"
                value={generateCount}
                onChange={setGenerateCount}
                disabled={loading || isGeneratingCSV}
              />
              <Button
                size="sm"
                onClick={generateShippingIds}
                className="!h-10 !rounded-xl"
                isLoading={loading || isGeneratingCSV}
              >
                Generate
              </Button>
            </div>
        </div>
        <DataTable
          columns={columns}
          title="Shipping IDs"
          data={finalFilteredData}
          isLoading={isLoading}
          showCheckbox={false}
        />
      </div>
      <AlertDialog />
    </RightWrapper>
  );
};

export default ShippingId;
